import type { FC, ReactElement, ReactNode } from "react";
import { useCallback } from "react";

import { colorsPalettes } from "@similarweb/styles";
import type { ISubsectionItem } from "@similarweb/ui-components";
import {
    EPrimaryItemType,
    EWarningMode,
    GroupPrimaryLevel,
    IconButton,
    SubsectionItem,
    Tooltip,
    WarningModeMessage,
} from "@similarweb/ui-components";
import type { ISubsectionItem as IUiSubsectionItem } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { ExpandableMenuItem } from "app/components/sidebar3/primary-side-bar/expandable-menu-item/ExpandableMenuItem";
import { LinkTextOverflow } from "app/components/sidebar3/primary-side-bar/styledComponents";
import type { IPrimarySidebarItem, ISubSectionItem } from "app/components/sidebar3/types";
import { EGroupTypes, ESidebarPlacement } from "app/components/sidebar3/types";
import { translate } from "app/filters/i18nFilter";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { mappingStateToId } from "app/reducers/_reducers/PrimarySidebar3Reducer";
import type { EPrimarySideBar3IdType } from "app/reducers/_reducers/PrimarySidebar3Reducer";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

import {
    IMPERSONATE_WARNING_TITLE,
    PACKAGE_DEMO_TOOL_TITLE,
    SIDEBAR_ALWAYS_OPENED_PAGES,
    SUBSCRIPTION_MANIPULATION_SUBTITLE,
    SUBSCRIPTION_MANIPULATION_TITLE,
    UI_BRANCH_TITLE,
    UI_VERSION_TITLE,
} from "./constants";
import type {
    IGetSidebarItemsComponents,
    IGetSingleItem,
    IGetWarningComponents,
    IProps,
    ISolutionLogoProps,
} from "./types";
import { swSettings } from "app/common/services/swSettings";
import type { IUser } from "app/@types/ISwSettings";

const PrimaryGroupSubsectionItem: FC<IUiSubsectionItem> = (props) => (
    <SubsectionItem
        {...props}
        isWithinActiveGroup={false}
    />
);

const getSingleItem = ({
    onComponentItemClick,
    isPrimarySidebarOpened,
    onSetIsBarOpenChange,
    handlePrimarySidebarOpenState,
    primaryBarData,
    onSetSelectedPrimarySectionChange,
    subSection, // sidebar item
    isActive,
    itemCounter,
}: IGetSingleItem): ReactNode => {
    const {
        title,
        icon,
        onClick,
        itemType,
        id,
        badgeData: badge,
        isLocked,
        onClickLocked,
        isHidden,
        upsellBadgeComponent,
    } = subSection;
    let props: IProps = { itemId: id, badgeData: badge };

    switch (itemType) {
        case EPrimaryItemType.StandalonePrimary:
            const standaloneItem = subSection as IPrimarySidebarItem;
            const { trackName, state } = standaloneItem;
            props = {
                ...props,
                state,
                trackName: trackName,
            };
            break;
        case EPrimaryItemType.GroupItem:
            const groupItem = subSection as ISubSectionItem;
            props = {
                ...props,
                state: groupItem.state,
                ParentTrackName: groupItem.ParentTrackName,
            };
            break;
        case EPrimaryItemType.Premium:
            props = { ...props, state: subSection.state };
            break;
        case EPrimaryItemType.SecondaryAction:
            const { sidebarItemComponent } = subSection as IPrimarySidebarItem;
            props = { ...props, trackName: subSection.trackName, sidebarItemComponent };
            break;
    }

    const { badgeData, state, itemId, sidebarItemComponent, ParentTrackName } = props;
    const onClickCallback = (id) => {
        if (isLocked) {
            onClickLocked();
        } else {
            onSetSelectedPrimarySectionChange(state, id);
            onClick(state, id, itemCounter, isLocked, badgeData?.badgeType, ParentTrackName);
        }
    };

    let componentToTender: ReactNode;

    if (isHidden) {
        componentToTender = null;
    } else if (sidebarItemComponent) {
        const onItemClick = () => {
            // Change tracking event name only for user settings component, otherwise keep default one
            const eventName = title === "userSettingsComponent" ? "settings and help" : undefined;
            onSetIsBarOpenChange(true, true);
            onComponentItemClick(props.trackName, itemCounter, eventName);
        };

        const onCloseSidebar = () => {
            handlePrimarySidebarOpenState();
        };

        componentToTender = sidebarItemComponent(isPrimarySidebarOpened, onItemClick, onCloseSidebar);
    } else {
        const subSectionItem = subSection as ISubSectionItem;
        const visibleChildren = subSectionItem.children?.filter((child) => !child.isHidden);

        if (visibleChildren?.length > 0) {
            componentToTender = (
                <ExpandableMenuItem
                    key={title}
                    parentItem={subSectionItem}
                    isPrimarySidebarOpened={isPrimarySidebarOpened}
                    primaryBarData={primaryBarData}
                    onSetSelectedPrimarySectionChange={onSetSelectedPrimarySectionChange}
                    itemCounter={itemCounter}
                />
            );
        } else {
            componentToTender = (
                <PrimaryGroupSubsectionItem
                    iconColor={colorsPalettes.indigo[300]}
                    key={title}
                    itemText={translate(title)}
                    itemType={itemType as EPrimaryItemType}
                    onItemClick={onClickCallback}
                    navBadgeType={badgeData?.badgeType}
                    badgeText={badgeData?.badgeText}
                    id={itemId}
                    icon={icon}
                    isCollapsed={!isPrimarySidebarOpened}
                    isSelected={primaryBarData.id === itemId || isActive}
                    isLocked={isLocked ? !upsellBadgeComponent : null}
                    badgeComponent={upsellBadgeComponent}
                />
            );
        }
    }

    return componentToTender;
};

export const getSidebarItemsComponents = ({
    primarySidebarItems,
    onComponentItemClick,
    isPrimarySidebarOpened,
    onSetIsBarOpenChange,
    handlePrimarySidebarOpenState,
    primaryBarData,
    onSetSelectedPrimarySectionChange,
    SecondarySideBar3Type,
}: IGetSidebarItemsComponents) => {
    const sidebarComponents = {
        topSidebar: { top: [], groups: [], bottom: [] },
        middleSidebar: [],
        bottomSidebar: [],
    };

    let itemCounter = 0;
    primarySidebarItems.forEach((sidebarItem) => {
        const { groupedSubSection, title, itemType, isHidden, placement, menuPlacement } = sidebarItem;
        let component;

        if (isHidden) {
            return null;
        }

        if (itemType === EGroupTypes.GroupPrimaryLevel) {
            const listOfGroupItems: ReactElement<ISubsectionItem>[] = groupedSubSection.map((subSection) => {
                if (!subSection.isHidden) {
                    itemCounter++;
                }
                const isActive = subSection.secondaryBarType3 === SecondarySideBar3Type;
                const result = getSingleItem({
                    subSection,
                    isPrimarySidebarOpened,
                    onSetIsBarOpenChange,
                    handlePrimarySidebarOpenState,
                    onSetSelectedPrimarySectionChange,
                    primaryBarData,
                    secondaryBarType3: subSection.secondaryBarType3,
                    isActive,
                    itemCounter,
                }) as ReactElement<ISubsectionItem>;

                const visibleChildrenCount = subSection.children?.filter((c) => !c.isHidden).length ?? 0;
                itemCounter += visibleChildrenCount;

                return result;
            });
            component = (
                <GroupPrimaryLevel
                    key={title}
                    selectedItemId={primaryBarData.id}
                    title={translate(title)}
                    isCollapsed={!isPrimarySidebarOpened}
                >
                    {listOfGroupItems}
                </GroupPrimaryLevel>
            );
            const visibleGroupedSubSection = groupedSubSection?.filter(({ isHidden }) => !isHidden);
            if (visibleGroupedSubSection.length > 0) {
                sidebarComponents.topSidebar.groups.push(component);
            }
        } else {
            const isActive = sidebarItem.secondaryBarType3 === SecondarySideBar3Type;
            if (!sidebarItem.isHidden) {
                itemCounter++;
            }
            component = getSingleItem({
                subSection: sidebarItem,
                onComponentItemClick,
                isPrimarySidebarOpened,
                handlePrimarySidebarOpenState,
                onSetIsBarOpenChange,
                primaryBarData,
                onSetSelectedPrimarySectionChange,
                isActive,
                itemCounter,
            });

            const visibleChildrenCount =
                (sidebarItem as ISubSectionItem).children?.filter((c) => !c.isHidden).length ?? 0;
            itemCounter += visibleChildrenCount;

            if (placement === ESidebarPlacement.Top) {
                menuPlacement === ESidebarPlacement.Top
                    ? sidebarComponents.topSidebar.top.push(component)
                    : sidebarComponents.topSidebar.bottom.push(component);
            } else if (placement === ESidebarPlacement.Middle) {
                sidebarComponents.middleSidebar.push(component);
            } else {
                sidebarComponents.bottomSidebar.push(component);
            }
        }
    });

    return sidebarComponents;
};

const UIBranch = (props: { branch: string; primarySidebarOpened: boolean }) => {
    const { showSuccessToast, showErrorToast } = useToasts();

    const onTextClick = useCallback(() => {
        const link = `${window.location.origin}/uibranch?branch=${props.branch}`;
        navigator.clipboard
            .writeText(link)
            .then(() => {
                showSuccessToast(`Copied to clipboard: ${link}`);
            })
            .catch(() => {
                showErrorToast("Failed to copy to clipboard");
            });
    }, [props.branch, showSuccessToast, showErrorToast]);

    return (
        <WarningModeMessage
            title={UI_BRANCH_TITLE}
            warningType={EWarningMode.UiVersion}
            isCollapsed={!props.primarySidebarOpened}
            icon={"sidebar3-ui-version"}
            subTitle={
                <Flex
                    height="14px"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Tooltip
                        content="Click to copy link to UI Branch"
                        delay={300}
                        placement="right"
                    >
                        <LinkTextOverflow
                            onClick={onTextClick}
                            cursor="pointer"
                            fontVariant="listSectionTitle"
                            color={colorsPalettes.carbon[400]}
                        >
                            {decodeURIComponent(props.branch)}
                        </LinkTextOverflow>
                    </Tooltip>
                </Flex>
            }
        />
    );
};

export const getWarningComponents = ({
    forUiVersion,
    forImpersonate,
    forPackageDemoTool,
    isPrimarySidebarOpened,
}: IGetWarningComponents) => {
    const warningsComponentsArray: ReactNode[] = [];
    const { uiVersion, branch } = forUiVersion;
    const { username, impersonateMode } = forImpersonate;
    const { claimsOverwritten, productsOverwritten, productsOverwrittenName: packageDemoTool } = forPackageDemoTool;

    if (impersonateMode) {
        const impersonateSource = (swSettings.user as IUser).impersonateSource;
        const impersonateSourceEmail: string = impersonateSource?.username ?? "";
        const backToLink = impersonateSource
            ? `${swSettings.swsites.pro}/useridentity/switchidentity?` +
              `name=${encodeURIComponent(impersonateSourceEmail)}&` +
              `userId=${impersonateSource.id}&` +
              `redirectTo=${encodeURIComponent(location.href)}`
            : "";
        const backToButton = (
            <Tooltip content={`Back to ${impersonateSourceEmail}`}>
                <IconButton
                    type="flat"
                    iconName="clear"
                    iconSize="xs"
                    onClick={() => {
                        window.location.href = backToLink;
                    }}
                />
            </Tooltip>
        );
        const ImpersonateComponent = (
            <WarningModeMessage
                title={IMPERSONATE_WARNING_TITLE}
                warningType={EWarningMode.Impersonate}
                isCollapsed={!isPrimarySidebarOpened}
                icon={"sidebar3-impersonate"}
                subTitle={username}
                action={backToButton}
            />
        );
        warningsComponentsArray.push(ImpersonateComponent);
    }
    if (uiVersion) {
        const UiVersionComponent = (
            <WarningModeMessage
                title={UI_VERSION_TITLE}
                warningType={EWarningMode.UiVersion}
                isCollapsed={!isPrimarySidebarOpened}
                icon={"sidebar3-ui-version"}
                subTitle={uiVersion}
            />
        );
        warningsComponentsArray.push(UiVersionComponent);
    }
    if (branch) {
        warningsComponentsArray.push(
            <UIBranch
                key={branch}
                branch={branch}
                primarySidebarOpened={isPrimarySidebarOpened}
            />,
        );
    }
    if (claimsOverwritten || productsOverwritten) {
        const ManipulationComponent = (
            <WarningModeMessage
                title={packageDemoTool ? PACKAGE_DEMO_TOOL_TITLE : SUBSCRIPTION_MANIPULATION_TITLE}
                warningType={packageDemoTool ? EWarningMode.PackageDemoTool : EWarningMode.SubscriptionManipulation}
                isCollapsed={!isPrimarySidebarOpened}
                icon={"sidebar3-package-manipulation"}
                subTitle={packageDemoTool ? packageDemoTool : SUBSCRIPTION_MANIPULATION_SUBTITLE}
            />
        );
        warningsComponentsArray.push(ManipulationComponent);
    }

    return warningsComponentsArray;
};

const getSubsectionId = (nameToMap: string): EPrimarySideBar3IdType => {
    return Object.keys(mappingStateToId).find((key) =>
        Array.isArray(mappingStateToId[key])
            ? mappingStateToId[key].includes(nameToMap)
            : mappingStateToId[key] === nameToMap,
    ) as EPrimarySideBar3IdType;
};

export const getInitialSubsectionId = (stateNameToMap: string, stateParentToMap: string): EPrimarySideBar3IdType => {
    const subsectionId = getSubsectionId(stateNameToMap);

    if (subsectionId) {
        return subsectionId;
    }

    return getSubsectionId(stateParentToMap);
};

export const getBaseUrl = (url) => {
    const urlObj = new URL(url);
    return `${urlObj.origin}`;
};

export function isSidebarAlwaysOpenedOnPage(pageId: EPrimarySideBar3IdType): boolean {
    return SIDEBAR_ALWAYS_OPENED_PAGES.includes(pageId);
}

export const isAppAnalysisPage = (currStateName = ""): boolean =>
    proNavigator.isAppAnalysis() ||
    !!currStateName.match(/appanalysis/) ||
    !!proNavigator.current().name.match(/appanalysis/);

export const getSolutionLogoProps = (currState?: IRouterState): Omit<ISolutionLogoProps, "onClick" | "isCollapsed"> => {
    if (proNavigator.isSalesSolution(currState)) {
        return {
            titleText: "Sales Intelligence",
            logoImagePath: "/images/sales-intel-logo.svg",
        };
    }

    if (isAppAnalysisPage(currState.name)) {
        return {
            titleText: "App Intelligence",
            logoImagePath: "/images/apps-intel-logo.svg",
        };
    }

    return {
        titleText: "Web Intelligence",
        logoImagePath: "/images/web-intel-logo.svg",
    };
};
